/** Enrollment pace: realized enrollments per active site per month vs the scenario rate lines. */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  SCENARIOS,
  useD3Chart,
  drawBandAxes,
  drawBandBars,
  monthKey,
  FONT,
  type Margin,
} from "./chartBase";
import type { EnrollmentPaceChartProps } from "./onboardingPlots.types";

export default function EnrollmentPaceChart({
  rows,
  height = 320,
}: EnrollmentPaceChartProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      const H = height;
      const m: Margin = { top: 16, right: 20, bottom: 42, left: 40 };
      const d = rows
        .filter((r) => r.enrolledPerSitePerMonth != null)
        .map((r) => ({ ...r, mm: monthKey(r.date) }));
      if (!d.length) return;

      const x = d3
        .scaleBand()
        .domain(d.map((r) => r.mm))
        .range([m.left, W - m.right])
        .padding(0.35);
      const y = d3
        .scaleLinear()
        .domain([
          0,
          Math.max(
            1,
            d3.max(d, (r) => r.enrolledPerSitePerMonth as number) || 1,
          ),
        ])
        .nice()
        .range([H - m.bottom, m.top]);
      drawBandAxes(svg, W, H, m, x, y);

      drawBandBars(
        svg,
        d,
        x,
        y,
        (r) => r.enrolledPerSitePerMonth as number,
        COLORS.paceBar,
        tip,
        (r) =>
          `<b>${r.date}</b><br>Enroll/site/mo: <b>${(r.enrolledPerSitePerMonth as number).toFixed(2)}</b>`,
      );

      SCENARIOS.forEach((s) => {
        svg
          .append("line")
          .attr("x1", m.left)
          .attr("x2", W - m.right)
          .attr("y1", y(s.rate))
          .attr("y2", y(s.rate))
          .attr("stroke", s.color)
          .attr("stroke-dasharray", "6 4")
          .attr("opacity", 0.8);
        svg
          .append("text")
          .attr("x", m.left + 4)
          .attr("y", y(s.rate) - 4)
          .attr("fill", s.color)
          .style("font-size", "10.5px")
          .style("font-weight", 600)
          .style("font-family", FONT)
          .text(`${s.label}`);
      });
    },
    [rows, height],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
